/**
 * Zentral definierte Query-Keys für TanStack Query.
 * Ermöglicht konsistente Invalidierung nach Mutations.
 * Enthält Keys für Offline-Bundle (Tier anlegen).
 */

/** Erstes Segment der Keys – für invalidateQueries({ queryKey: prefix }) */
export const queryKeyPrefixes = {
  entitySections: ["entitySections"] as const,
  entityFields: ["entityFields"] as const,
  sectionsFieldsTree: ["sectionsFieldsTree"] as const,
} as const;

export const queryKeys = {
  profile: ["profile"] as const,
  organizations: {
    my: (userId: string) => ["organizations", "my", userId] as const,
  },
  invitations: {
    forOrg: (organizationId: string) => ["invitations", organizationId] as const,
    none: ["invitations", "none"] as const,
  },
  profileFlags: {
    isSuperadmin: ["profile", "is_superadmin"] as const,
  },
  members: {
    all: ["members"] as const,
    list: (userId: string, orgId: string) =>
      ["members", "list", userId, orgId] as const,
    directory: (userId: string, orgId: string) =>
      ["members", "directory", userId, orgId] as const,
  },
  intake: {
    lastCareSite: (userId: string, organizationId: string) =>
      ["intake", "lastCareSite", userId, organizationId] as const,
  },
  careSites: {
    my: ["careSites", "my"] as const,
    myAccess: (userId: string) => ["careSites", "myAccess", userId] as const,
    detail: (siteId: string) => ["careSites", "detail", siteId] as const,
    nodes: (siteId: string) => ["careSites", "nodes", siteId] as const,
    access: (siteId: string) => ["careSites", "access", siteId] as const,
    canManage: (siteId: string) => ["careSites", "canManage", siteId] as const,
  },
  caseTable: {
    list: (filterKey: string) => ["caseTable", "list", filterKey] as const,
    savedViews: ["caseTable", "savedViews"] as const,
    bookmark: (caseId: string) => ["caseTable", "bookmark", caseId] as const,
  },
  comments: {
    forCase: (caseId: string) => ["comments", "case", caseId] as const,
    mentionable: (caseId: string) => ["comments", "mentionable", caseId] as const,
  },
  cases: {
    my: ["cases", "my"] as const,
    detail: (caseId: string) => ["cases", "detail", caseId] as const,
    intakeCatalog: (orgId: string) => ["cases", "intakeCatalog", orgId] as const,
    intakeReasonIds: (caseId: string) =>
      ["cases", "intakeReasonIds", caseId] as const,
    relations: (caseId: string) => ["cases", "relations", caseId] as const,
    sameIdentity: (caseId: string) =>
      ["cases", "sameIdentity", caseId] as const,
  },
  contacts: {
    caseFinder: (caseId: string) => ["contacts", "caseFinder", caseId] as const,
  },

  animals: {
    my: ["animals", "my"] as const,
    tenant: (tenantId: string) => ["animals", "tenant", tenantId] as const,
    detail: (tenantId: string, animalId: string) =>
      ["animals", "detail", tenantId, animalId] as const,
    photos: (tenantId: string, animalId: string) =>
      ["animals", "photos", tenantId, animalId] as const,
    dueCareCount: (locationIds: string[]) =>
      ["animals", "dueCareCount", ...locationIds.sort()] as const,
  },
  entitySections: {
    list: (tenantId: string, tenantSpeciesId: string) =>
      ["entitySections", tenantId, tenantSpeciesId] as const,
  },
  sectionsFieldsTree: (tenantId: string, tenantSpeciesId: string) =>
    ["sectionsFieldsTree", tenantId, tenantSpeciesId] as const,
  entityFields: {
    definition: (
      tenantId: string,
      entityCode: string,
      variantCode: string,
      scope: string,
      tenantSpeciesId?: string
    ) =>
      [
        "entityFields",
        "definition",
        tenantId,
        entityCode,
        variantCode,
        scope,
        tenantSpeciesId ?? "",
      ] as const,
    list: (tenantId: string, tenantSpeciesId: string) =>
      ["entityFields", "list", tenantId, tenantSpeciesId] as const,
  },
  species: {
    /** Nur aktive – für "Neues Tier" Formular */
    tenant: (tenantId: string) => ["species", "tenant", tenantId] as const,
    /** Alle (inkl. inaktive) – für Admin-Settings */
    tenantAdmin: (tenantId: string) =>
      ["species", "tenantAdmin", tenantId] as const,
    /** Aktive Arten des Vereins. Die Aufnahme setzt code unknown in der Anzeige nach hinten. */
    organization: (organizationId: string) =>
      ["species", "organization", organizationId] as const,
  },
  animalNumbering: {
    rule: (tenantId: string, tenantSpeciesId: string) =>
      ["animalNumbering", "rule", tenantId, tenantSpeciesId] as const,
    list: (tenantId: string) =>
      ["animalNumbering", "list", tenantId] as const,
  },
  locations: {
    my: ["locations", "my"] as const,
    pflegeorteTree: ["locations", "pflegeorte-tree"] as const,
    /** Baum für Pflegebereich: eigene Orte + alle Vereinsorte der Vereine des Users */
    pflegebereichTree: ["locations", "pflegebereich-tree"] as const,
    /** Baum der Vereins-Pflegeorte (owner_tenant_id = tenantId) */
    pflegeorteTreeForTenant: (tenantId: string) =>
      ["locations", "pflegeorte-tree-tenant", tenantId] as const,
    members: (locationId: string) =>
      ["locations", "members", locationId] as const,
    shareStatus: (locationId: string) =>
      ["locations", "shareStatus", locationId] as const,
    assignable: (tenantId: string) =>
      ["locations", "assignable", tenantId] as const,
    careReminderIds: ["locations", "care-reminder-ids"] as const,
    /** IDs der Orte, an denen der aktuelle User Mitwirkung hat (location_memberships). */
    idsWhereIAmStaff: ["locations", "ids-where-i-am-staff"] as const,
    isInternal: (tenantId: string, locationId: string) =>
      ["locations", "isInternal", tenantId, locationId] as const,
    units: (locationId: string) =>
      ["locations", "units", locationId] as const,
    unitsForPicker: (locationId: string) =>
      ["locations", "unitsForPicker", locationId] as const,
  },
  tenants: {
    my: ["tenants", "my"] as const,
    members: (tenantId: string) =>
      ["tenants", "members", tenantId] as const,
    profile: (tenantId: string) =>
      ["tenants", "profile", tenantId] as const,
  },
  notifications: {
    active: (tenantIds: string[]) =>
      ["notifications", "active", ...tenantIds.sort()] as const,
    countNew: (tenantIds: string[]) =>
      ["notifications", "countNew", ...tenantIds.sort()] as const,
  },
  careSchedules: {
    dueList: (tenantIds: string[], locationIds: string[]) =>
      ["careSchedules", "dueList", ...tenantIds.sort(), ...locationIds.sort()] as const,
  },
  careLog: {
    entries: (tenantId: string, animalId: string) =>
      ["careLog", "entries", tenantId, animalId] as const,
    activityTypes: (tenantId: string) =>
      ["careLog", "activityTypes", tenantId] as const,
  },
  careActivityTypes: {
    listAll: (tenantId: string) => ["careActivityTypes", "listAll", tenantId] as const,
  },
  treatments: {
    list: (tenantId: string) => ["treatments", "list", tenantId] as const,
    listAll: (tenantId: string) => ["treatments", "listAll", tenantId] as const,
  },
  diagnoses: {
    list: (tenantId: string) => ["diagnoses", "list", tenantId] as const,
    listAll: (tenantId: string) => ["diagnoses", "listAll", tenantId] as const,
  },
  diagnosisTreatmentPolicies: {
    list: (tenantId: string) =>
      ["diagnosisTreatmentPolicies", "list", tenantId] as const,
  },
  diagnosisTreatmentDoseRules: {
    list: (tenantId: string) =>
      ["diagnosisTreatmentDoseRules", "list", tenantId] as const,
  },
  animalDiagnoses: {
    active: (tenantId: string, animalId: string) =>
      ["animalDiagnoses", "active", tenantId, animalId] as const,
    all: (tenantId: string, animalId: string) =>
      ["animalDiagnoses", "all", tenantId, animalId] as const,
  },
  club: {
    feed: (organizationId: string) => ["club", "feed", organizationId] as const,
    item: (itemId: string) => ["club", "item", itemId] as const,
    comments: (itemId: string) => ["club", "comments", itemId] as const,
    events: (organizationId: string, from: string, to: string) =>
      ["club", "events", organizationId, from, to] as const,
    search: (organizationId: string, query: string, typesKey: string) =>
      ["club", "search", organizationId, query, typesKey] as const,
    caseChips: (idsKey: string) => ["club", "caseChips", idsKey] as const,
    memberMini: (profileId: string) => ["club", "memberMini", profileId] as const,
  },
} as const;
